import getClientSuggestions from './getSuggestions/getClientSuggestions';
import getServerSuggestions from './getSuggestions/getServerSuggestions';
import getZeroQuerySuggestions from './getSuggestions/getZeroQuerySuggestions';
import { logClientSuggestions } from './getSuggestions/logClientSuggestions';
import orderSuggestions from './getSuggestions/orderSuggestions';
import { DEBOUNCE_DELAY_MS } from '../constants';
import getSuggestionQueryString from '../utils/getSuggestionQueryString';
import getSupportedSuggestionKinds from '../utils/getSupportedSuggestionKinds';
import isMailSearchAction from '../utils/isMailSearchAction';
import { ActionDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { getAggregationBucket } from 'owa-analytics-actions';
import { getGuid } from 'owa-guid';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { SearchScenarioId } from 'owa-search-next';
import { SuggestionSet } from 'owa-search-service';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { orchestrator } from 'satcheljs';
import {
    getSuggestions,
    lazyGetSearchStore,
    lazyIsSearchBoxEmpty,
    onServiceResponse,
    onServiceRequest,
    onSuggestionsProcessed,
    lazySetLatestRenderedQFTraceId,
} from 'owa-search-next-lazy';
import isInLazySearchExperiment from '../utils/isInLazySearchExperiment';

export default orchestrator(getSuggestions, async actionMessage => {
    const { scenarioId } = actionMessage;

    /**
     * DevOps 38525: Move the suggestion - generation code into its own package
     * so it can be reused by fileshub
     */
    if (!isMailSearchAction(scenarioId) && scenarioId !== SearchScenarioId.FilesHub) {
        return;
    }

    // If explicit logon / delegate access, don't provide suggestions.
    if (getUserConfiguration().SessionSettings.IsExplicitLogon) {
        return;
    }

    const datapoint = returnTopExecutingActionDatapoint();
    datapoint.addCustomData([scenarioId]);

    const queryString = await getSuggestionQueryString(scenarioId);
    datapoint.addCustomData([getAggregationBucket({ value: queryString.length })]);

    /**
     * If we have a query string (so we're not in zero query scenario) and
     * QF is enabled, generate a GUID to serve as the request ID and set it in
     * the store.
     */
    let currentQfRequestId = null;
    if (
        (queryString || (is3SFeatureEnabled('sea-zinq-3s3') || isInLazySearchExperiment())) &&
        is3SFeatureEnabled('sea-qf')
    ) {
        currentQfRequestId = getGuid();
        onServiceRequest(SearchScenarioId.Mail, currentQfRequestId);
    }

    const getSuggestionsInternalParameters = {
        currentQfRequestId,
        queryString,
        scenarioId,
        datapoint,
    };

    return new Promise<void>(resolve => {
        // Wait DEBOUNCE_DELAY_MS before checking if we should get new suggestions.
        setTimeout(async () => {
            await getSuggestionsInternal(getSuggestionsInternalParameters);
            resolve();
        }, DEBOUNCE_DELAY_MS);
    });
});

async function getSuggestionsInternal({
    currentQfRequestId,
    queryString,
    scenarioId,
    datapoint,
}: {
    queryString: string;
    scenarioId: SearchScenarioId;
    currentQfRequestId: string;
    datapoint: ActionDatapoint;
}): Promise<void> {
    const shouldGetSuggestionsResult = await shouldGetSuggestions({
        queryString: queryString,
        scenarioId: scenarioId,
        currentQfRequestId: currentQfRequestId,
    });

    datapoint.addCustomData([shouldGetSuggestionsResult]);

    // Don't get suggestions if we shouldn't get suggestions.
    if (!shouldGetSuggestionsResult) {
        return;
    }

    // Get suggestion kinds that given scenario supports.
    const supportedSuggestionKinds = getSupportedSuggestionKinds(scenarioId);

    /**
     * If the trimmed query string is equivalent to an empty string, get zero
     * query suggestions.
     *
     * - "", " ", "   " will result in zero query suggestions.
     * - "o", "o ", "o    " will result in a service call to fetch suggestions.
     */
    if (
        queryString.trim() === '' &&
        !(is3SFeatureEnabled('sea-zinq-3s3') || isInLazySearchExperiment())
    ) {
        // Clear latestTraceId from store since client suggestions are shown.
        onServiceResponse(scenarioId, null, true /* isQFTraceId */);

        if (is3SFeatureEnabled('sea-zinq-3sctr3')) {
            // Issue request to get server suggestions.
            getServerSuggestions(scenarioId, queryString).then(async function(
                serverSuggestionsSet
            ) {
                // set the trace id for this request so that session
                // instrumentation will be correct
                const setLatestRenderedQFTraceId = await lazySetLatestRenderedQFTraceId.import();
                setLatestRenderedQFTraceId(serverSuggestionsSet.TraceId, scenarioId);
            });
        }

        let zeroQuerySuggestions = await getZeroQuerySuggestions();
        zeroQuerySuggestions = zeroQuerySuggestions.filter(suggestion =>
            supportedSuggestionKinds.includes(suggestion.kind)
        );

        onSuggestionsProcessed(
            scenarioId,
            { Suggestions: zeroQuerySuggestions, IsComplete: true, TraceId: null },
            null /* qfRequestId */
        );

        return;
    }

    // Issue request to get server suggestions.
    const serverSuggestionSetPromise: Promise<SuggestionSet> = getServerSuggestions(
        scenarioId,
        queryString
    );

    // Get client suggestions.
    const clientSuggestions = getClientSuggestions(queryString);

    // Wait for server suggestions, merge, and put them in the correct order.
    const serverSuggestionsSet = await serverSuggestionSetPromise;
    const serverSuggestions = serverSuggestionsSet.Suggestions;

    /**
     * Continue if the search box isn't empty. If it's empty,
     * we don't want to override zero query suggestions.
     *
     * when trace ids are equal, it means getServerSuggestions returned what's
     * already in the store
     * an example is when QF calls A, B, C goes out,
     * and the response comes back out of order as C, B, A,
     * getServerSuggestions B and A will end up return back suggestion set C in store
     * because it's the latest suggestions set user wants
     */
    const isSearchBoxEmpty = await lazyIsSearchBoxEmpty.import();
    const getSearchStore = await lazyGetSearchStore.import();
    const searchStore = getSearchStore(scenarioId);
    const currentSuggestions = searchStore.currentSuggestions;
    if (
        (!isSearchBoxEmpty(scenarioId) ||
            (is3SFeatureEnabled('sea-zinq-3s3') || isInLazySearchExperiment())) &&
        (!currentSuggestions || currentSuggestions.TraceId != serverSuggestionsSet.TraceId)
    ) {
        // Log client suggestions after the QF trace ID comes back.
        logClientSuggestions(
            serverSuggestionsSet.TraceId,
            clientSuggestions.instrumentationContext
        );

        // Order and filter suggestions (based on supported suggestions).
        let suggestions = orderSuggestions(serverSuggestions.concat(clientSuggestions.suggestions));
        suggestions = suggestions.filter(suggestion =>
            supportedSuggestionKinds.includes(suggestion.kind)
        );

        onSuggestionsProcessed(
            scenarioId,
            {
                Suggestions: suggestions,
                IsComplete: true,
                TraceId: serverSuggestionsSet.TraceId,
            },
            currentQfRequestId
        );
    }
}

/**
 * Do not fetch new suggestions if:
 * - Query text has been changed
 * - Too many requests have been issued in succession (i.e. user typing away)
 *   - The exception here is zero query. Because we clear the currentQfRequestId
 *     (because zero query isn't served by QF), it won't be equal to
 *     the latest QF request ID in the store.
 */
async function shouldGetSuggestions({
    queryString,
    currentQfRequestId,
    scenarioId,
}: {
    queryString: string;
    currentQfRequestId: string;
    scenarioId: SearchScenarioId;
}): Promise<boolean> {
    const getSearchStore = await lazyGetSearchStore.import();
    const currentSuggestionQueryString = await getSuggestionQueryString(scenarioId);

    return (
        queryString === currentSuggestionQueryString &&
        (getSearchStore(scenarioId).latestQFRequestId === currentQfRequestId ||
            currentQfRequestId === null)
    );
}
